import { inject, Injectable } from '@angular/core';
import { isDefined } from '@sama/js-core';
import { InjectableSuperclass } from '@sama/ng-core';
import { isUndefined } from 'lodash';
import { combineLatest, filter } from 'rxjs';
import { OrthographicCamera } from 'three';
import { isOrthographicVideoOutput } from '../../../cross-team-shared/misc-utils/output-utils';
import { assertDefined } from '../../../cross-team-shared/misc-utils/strict-record';
import { ViewBox } from '../../../team-apis/content/view-box';
import { Point2D } from '../../../team-apis/otterdam/answers/shape-typings';
import { PointCloudContentStore } from '../../state/point-cloud-content-store';
import { RendererLayer, setRendererLayers } from '../render-utils';

@Injectable()
export class OrthographicCameraService extends InjectableSuperclass {
  private readonly store = inject(PointCloudContentStore);

  camera = new OrthographicCamera(0, 0, 0, 0);

  constructor() {
    super();

    if (isUndefined(this.getOrhographicOutput())) {
      return;
    }

    setRendererLayers([RendererLayer.Orthographic], this.camera);
    this.subscribeToAssetBoundingBox();
    this.subscribeToViewBox();
  }

  private subscribeToAssetBoundingBox() {
    this.subscribeTo(
      this.store('assetBoundingBox').$.pipe(filter(isDefined)),
      ({ min, max }) => {
        this.camera.position.setZ(max[2]);
        this.camera.near = 0;
        this.camera.far = Math.abs(max[2] - min[2]);
        this.camera.updateProjectionMatrix();
      },
    );
  }

  private subscribeToViewBox() {
    const output = this.getOrhographicOutput();
    assertDefined(output);

    this.subscribeTo(
      combineLatest([
        this.store('render')('containerSize')(output.name).$.pipe(
          filter(isDefined),
        ),
        this.store('viewBoxes')(output.name).$.pipe(filter(isDefined)),
      ]),
      ([size, viewBox]) => {
        this.updateCamera(size, viewBox);
      },
    );
  }

  private updateCamera(size: Point2D, viewBox: ViewBox) {
    const aspectRatio = size[0] / size[1];
    const shouldAdjustWidth = viewBox[2] / viewBox[3] < aspectRatio;

    let viewWidth = viewBox[2];
    let viewHeight = viewBox[3];
    if (shouldAdjustWidth) {
      viewWidth = viewBox[3] * aspectRatio;
    } else {
      viewHeight = viewBox[2] / aspectRatio;
    }

    const canvasTopLeft: Point2D = [viewBox[0], viewBox[1]];
    const cameraTopLeft = this.canvasToWebGlCoordinateSystem(canvasTopLeft);

    this.camera.left = cameraTopLeft[0];
    this.camera.right = cameraTopLeft[0] + viewWidth;
    this.camera.top = cameraTopLeft[1];
    this.camera.bottom = cameraTopLeft[1] - viewHeight;

    this.camera.updateProjectionMatrix();
  }

  private canvasToWebGlCoordinateSystem(point: Point2D): Point2D {
    // By default, the canvas origin ([0, 0]) is the top-left corner. Where Y
    // values increases as the point goes down the screen. Whereas, in our 3D
    // system the camera is oriented so that the XY plane has the Y value
    // increasing as the point goes up the screen. Therefore, we just negate the
    // Y value to convert between coordinate systems, where the origin is the
    // same.
    return [point[0], -point[1]];
  }

  private getOrhographicOutput() {
    return this.store.state().project.outputs.find(isOrthographicVideoOutput);
  }
}
